import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Chip, IconButton, Button, MenuItem, Select, FormControl, InputLabel, Pagination,
} from '@mui/material';
import { Visibility as ViewIcon } from '@mui/icons-material';
import { userAdminAPI } from '../services/api';
import { formatKstDate } from '../utils/datetime';

const fmt = (n: number) => new Intl.NumberFormat('ko-KR').format(n);

const Users: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [suspended, setSuspended] = useState('');
  const [page, setPage] = useState(1);
  const limit = 20;

  const { data, isLoading } = useQuery({
    queryKey: ['admin-users', search, suspended, page],
    queryFn: async () => (await userAdminAPI.getAll({ search, suspended: suspended || undefined, page, limit })).data,
  });

  const totalPages = data ? Math.ceil(data.total / limit) : 1;

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>회원 관리</Typography>

      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          size="small" label="이메일/닉네임 검색" value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { setSearch(searchInput); setPage(1); } }}
        />
        <Button variant="contained" onClick={() => { setSearch(searchInput); setPage(1); }}>검색</Button>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>상태</InputLabel>
          <Select value={suspended} label="상태" onChange={(e) => { setSuspended(e.target.value); setPage(1); }}>
            <MenuItem value="">전체</MenuItem>
            <MenuItem value="false">정상</MenuItem>
            <MenuItem value="true">정지</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>이메일/닉네임</TableCell>
              <TableCell>로그인</TableCell>
              <TableCell align="right">직급</TableCell>
              <TableCell align="right">보유 포인트</TableCell>
              <TableCell align="right">보너스</TableCell>
              <TableCell>상태</TableCell>
              <TableCell>가입일</TableCell>
              <TableCell align="center">상세</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.items?.map((u) => (
              <TableRow key={u.id} hover>
                <TableCell>{u.id}</TableCell>
                <TableCell>{u.nickname || u.email || '-'}</TableCell>
                <TableCell>{u.provider}</TableCell>
                <TableCell align="right">Lv.{u.level}</TableCell>
                <TableCell align="right">{fmt(u.points)}</TableCell>
                <TableCell align="right">{fmt(u.bonusPoints)}</TableCell>
                <TableCell>
                  <Chip label={u.isSuspended ? '정지' : '정상'} color={u.isSuspended ? 'error' : 'success'} size="small" />
                </TableCell>
                <TableCell>{formatKstDate(u.createdAt)}</TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => navigate(`/users/${u.id}`)}><ViewIcon /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            {!isLoading && !data?.items?.length && (
              <TableRow><TableCell colSpan={9} align="center">회원이 없습니다.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} />
        </Box>
      )}
    </Box>
  );
};

export default Users;
